import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { listGasStations, updateDeliveryDriver } from '@vn-propane/shared/client';
import type { GasStation, ErrorResponse } from '@vn-propane/shared/client';
import { useDeliveryDriver } from '../DeliveryDriversLayout';
import { Building2 } from 'lucide-react';

export default function DeliveryDriverAssignments() {
  const { id: orgId } = useParams<{ id: string }>();
  const { t } = useTranslation(['admin', 'common']);
  const { driver, refreshDrivers } = useDeliveryDriver();
  const [stations, setStations] = useState<GasStation[]>([]);
  const [selectedStationIds, setSelectedStationIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!orgId) return;

    const fetchStations = async () => {
      setLoading(true);
      const result = await listGasStations({ path: { org_id: orgId } });
      if (result.data) {
        setStations(result.data.data);
      }
      setLoading(false);
    };

    fetchStations();
  }, [orgId]);

  useEffect(() => {
    if (driver) {
      setSelectedStationIds(driver.gas_station_ids);
    }
  }, [driver]);

  const handleToggleStation = (stationId: string) => {
    setSelectedStationIds((prev) =>
      prev.includes(stationId)
        ? prev.filter((id) => id !== stationId)
        : [...prev, stationId]
    );
  };

  const handleSave = async () => {
    if (!orgId || !driver) return;

    setSaving(true);
    const result = await updateDeliveryDriver({
      path: { org_id: orgId, id: driver.id },
      body: { gas_station_ids: selectedStationIds },
    });

    if (result.error) {
      const errorResponse = result.error as ErrorResponse;
      console.error('Failed to update assignments:', errorResponse.error?.message);
    } else {
      refreshDrivers();
    }
    setSaving(false);
  };

  const hasChanges =
    driver &&
    (selectedStationIds.length !== driver.gas_station_ids.length ||
      !selectedStationIds.every((id) => driver.gas_station_ids.includes(id)));

  if (!driver) {
    return null;
  }

  if (loading) {
    return <div className="flex justify-center p-8">{t('deliveryDrivers.loadingStations')}</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">{t('deliveryDrivers.gasStationAssignments')}</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {t('deliveryDrivers.selectStationsDescription')}
        </p>
      </div>

      {stations.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>{t('deliveryDrivers.noStationsAvailable')}</p>
          <p className="text-sm">{t('deliveryDrivers.createStationsFirst')}</p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {stations.map((station) => (
              <label
                key={station.id}
                className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
              >
                <Checkbox
                  checked={selectedStationIds.includes(station.id)}
                  onCheckedChange={() => handleToggleStation(station.id)}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{station.name}</div>
                  {station.addressId && (
                    <div className="text-sm text-muted-foreground truncate">
                      {t('gasStation.hasAddress')}
                    </div>
                  )}
                </div>
              </label>
            ))}
          </div>

          <div className="flex items-center gap-4 pt-4 border-t">
            <Button onClick={handleSave} disabled={!hasChanges || saving}>
              {saving ? t('common:buttons.saving') : t('deliveryDrivers.saveAssignments')}
            </Button>
            {hasChanges && (
              <span className="text-sm text-muted-foreground">{t('deliveryDrivers.unsavedChanges')}</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
